/**
 * Which Admin sections a caller sees (Phase 7b): the tenant admin's (needs
 * `tenant.admin` tenant-wide, as the routes do) and usage (`billing.view`).
 * Unknown grants (a node's page) offer everything, as `lib/permissions.ts`
 * does; the server decides.
 */
import { allows, allowsSomewhere, type Grants } from '../permissions.js';

export type AdminSectionLink = { readonly label: string; readonly href: string };

const TENANT_WIDE = { siteId: null, studioId: null } as const;

export const ADMIN_HOME = '/admin';

export function isTenantAdmin(grants: Grants): boolean {
  return allows(grants, 'tenant.admin', TENANT_WIDE);
}

export function adminSections(grants: Grants): AdminSectionLink[] {
  const admin = isTenantAdmin(grants)
    ? [
        { label: 'Users', href: '/admin/users' },
        { label: 'Groups', href: '/admin/groups' },
        { label: 'Sites & nodes', href: '/admin/sites' },
        { label: 'Device secrets', href: '/admin/secrets' },
        { label: 'Audit log', href: '/admin/audit' },
      ]
    : [];
  const billing = allowsSomewhere(grants, 'billing.view') ? [{ label: 'Usage & cost', href: '/admin/usage' }] : [];
  return [...admin, ...billing];
}

/** Where `/admin` goes: the first section the caller may see, or the area itself (which then says there is none). */
export function adminLanding(grants: Grants): string {
  return adminSections(grants)[0]?.href ?? ADMIN_HOME;
}

/** Whether the top bar shows "Admin" at all. */
export function showsAdmin(grants: Grants): boolean {
  return adminSections(grants).length > 0;
}
